import React, { useId } from 'react';
import { Modal, ModalSize } from './Modal';
import { Button, ButtonVariant } from '@/shared/ui/Button';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/hooks/useTheme';
import { AlertTriangle, Trash2, LogOut } from 'lucide-react';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  confirmVariant?: ButtonVariant;
  hideIcon?: boolean;
  size?: ModalSize;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  isLoading,
  confirmVariant = ButtonVariant.Destructive,
  hideIcon = false,
  size = ModalSize.Sm,
}) => {
  const { t } = useTranslation();
  useTheme();
  const titleId = useId();

  // Choose icon based on action type
  const isDelete = confirmVariant === ButtonVariant.Destructive;
  const isUnsavedChanges = message.toLowerCase().includes('unsaved') || message.toLowerCase().includes('discard');
  const Icon = isDelete && !isUnsavedChanges ? Trash2 : isUnsavedChanges ? LogOut : AlertTriangle;
  const iconBg = isDelete ? 'bg-error/10' : isUnsavedChanges ? 'bg-warning/10' : 'bg-warning/10';
  const iconColor = isDelete ? 'text-error' : isUnsavedChanges ? 'text-warning' : 'text-warning';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size={size} overlayClassName="z-[9999]" preventCloseOnBackdrop ariaLabelledBy={titleId}>
      <div className="text-center">
        {/* Icon — hidden when hideIcon is true */}
        {!hideIcon && (
          <div className={`mx-auto w-14 h-14 rounded-full ${iconBg} flex items-center justify-center mb-4`}>
            <Icon className={`w-7 h-7 ${iconColor}`} aria-hidden="true" />
          </div>
        )}

        {/* Title */}
        <h3 id={titleId} className="text-lg font-semibold text-text mb-2">{title}</h3>

        {/* Message */}
        <p className="text-sm text-text-muted mb-6 px-4">{message}</p>

        {/* Buttons */}
        <div className="flex gap-3 justify-center pt-4 border-t border-border">
          <Button
            variant={ButtonVariant.Outline}
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 max-w-[160px] h-10 text-sm font-medium"
            data-testid="cancel-button"
          >
            {cancelText || (isUnsavedChanges ? t('common.unsavedChanges.stay', 'Stay') : t('global.cancel'))}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={onConfirm}
            disabled={isLoading}
            data-testid="confirm-button"
            className="flex-1 max-w-[200px] h-10 text-sm font-medium whitespace-nowrap"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-current border-t-transparent" />
                {confirmText || t('global.deleting')}
              </span>
            ) : (
              confirmText || (isUnsavedChanges ? t('common.unsavedChanges.leave', 'Discard') : t('global.delete'))
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmationModal;